import type { JsonObject } from '../../../types/json.types'
import { ActivityVisibility, DifficultyLevel } from '../../../enums'
import {
  IsString,
  IsOptional,
  IsUUID,
  IsObject,
  IsDateString,
  IsEnum,
  IsArray,
  IsNumber,
  IsNotEmpty,
  ValidateNested,
} from 'class-validator'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { Type } from 'class-transformer'
import { ActivityTeachingAssignmentDto } from './assignment/teaching-assignment.dto'

export class ActivityCompetencyLinkDto {
  @ApiProperty({ description: 'ID de la compétence programme', format: 'uuid' })
  @IsUUID()
  programCompetencyId!: string

  @ApiProperty({ description: 'Niveau ciblé (code référentiel)' })
  @IsString()
  targetLevel!: string

  @ApiPropertyOptional({
    description: "Poids de la compétence dans l'activité (1-5)",
    example: 3,
    default: 3,
    minimum: 1,
    maximum: 5,
  })
  @IsOptional()
  @IsNumber()
  weight?: number
}

export class CreateActivityDto {
  @ApiProperty({
    type: 'string',
    description: "ID de l'établissement",
    format: 'uuid',
  })
  @IsOptional() // Optional because it might be inferred from context
  @IsUUID()
  establishmentId?: string

  @ApiPropertyOptional({
    description: 'IDs des leçons à relier',
    type: [String],
    example: ['550e8400-e29b-41d4-a716-446655440000'],
  })
  @IsOptional()
  @IsArray()
  @IsUUID(undefined, { each: true })
  lessonIds?: string[]

  @ApiPropertyOptional({
    description: "Description de l'activité",
    type: 'string',
  })
  @IsOptional()
  @IsString()
  description?: string

  @ApiProperty({
    description: "Titre de l'activité",
    type: 'string',
  })
  @IsNotEmpty()
  @IsString()
  title!: string

  @ApiPropertyOptional({
    description:
      'Contenu JSON de l’activité (doit respecter le jsonSchema du type)',
    type: 'object',
    additionalProperties: true,
  })
  @IsOptional()
  @IsObject()
  content?: JsonObject

  @ApiPropertyOptional({
    description: 'Configuration de rendu/lecture',
    type: 'object',
    additionalProperties: true,
  })
  @IsOptional()
  @IsObject()
  settings?: JsonObject

  @ApiProperty({
    description: "ID de l'état initial",
    format: 'uuid',
    type: 'string',
  })
  @IsOptional()
  @IsUUID()
  stateId?: string

  @ApiPropertyOptional({
    description: 'Date de publication',
    type: 'string',
  })
  @IsOptional()
  @IsDateString()
  publishedAt?: string

  @ApiPropertyOptional({
    description: 'ID du template utilisé',
    format: 'uuid',
    type: 'string',
  })
  @IsOptional()
  @IsUUID()
  templateId?: string

  @ApiProperty({
    description: "ID du type d'activité",
    format: 'uuid',
    type: 'string',
  })
  @IsNotEmpty()
  @IsUUID()
  typeId!: string

  @ApiPropertyOptional({
    enum: ActivityVisibility,
    description: 'Visibilité de la ressource',
  })
  @IsOptional()
  @IsEnum(ActivityVisibility)
  visibility?: ActivityVisibility

  @ApiPropertyOptional({
    description: 'Tags associés',
    type: [String],
  })
  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  tags?: string[]

  @ApiPropertyOptional({
    enum: DifficultyLevel,
    description: 'Niveau de difficulté',
  })
  @IsOptional()
  @IsEnum(DifficultyLevel)
  difficulty?: DifficultyLevel

  @ApiPropertyOptional({
    description: "Instructions pour l'élève",
    type: 'string',
  })
  @IsOptional()
  @IsString()
  instructions?: string

  @ApiPropertyOptional({
    description: 'Configuration technique (JSON)',
    type: 'object',
    additionalProperties: true,
  })
  @IsOptional()
  @IsObject()
  config?: JsonObject

  @ApiPropertyOptional({
    description: 'Solution attendue (corrigé)',
    type: 'object',
    additionalProperties: true,
  })
  @IsOptional()
  @IsObject()
  solution?: JsonObject

  @ApiPropertyOptional({
    description: 'Assignations enseignement à créer en même temps',
    type: () => [ActivityTeachingAssignmentDto],
  })
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ActivityTeachingAssignmentDto)
  teachingAssignments?: ActivityTeachingAssignmentDto[]

  @ApiPropertyOptional({
    description: 'Durée estimée en minutes',
    type: Number,
  })
  @IsOptional()
  @IsNumber()
  estimatedMinutes?: number


  @ApiPropertyOptional({
    description: 'Liens compétences à créer',
    type: () => [ActivityCompetencyLinkDto],
  })
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ActivityCompetencyLinkDto)
  competencyLinks?: ActivityCompetencyLinkDto[]
}
